import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { unwrapGridEnvelope } from '@sophocles/data-grids/datasource/fetchAllPages';
import { buildGridListParams, toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import usePersistedState from '@sophocles/hooks/usePersistedState';

const GRID_IDX_KEY = '_gridIdx';

const LAZY_PAGE_SIZE = 50;
const EMPTY_OBJECT = {};

/**
 * Reusable hook for DataGrid Pro server-side data via the `dataSource` API.
 *
 * Uses MUI X v8's `dataSource` + `lazyLoading` for infinite scroll, or
 * `dataSource` with standard pagination. The grid manages all row state,
 * loading indicators, request deduplication, and caching internally.
 *
 * Existing `fetchFn` functions (which accept `{ limit, offset, o, text_search, ... }`
 * and return `{ results, count }`) work without changes.
 *
 * @param {Object} options
 * @param {Function} options.fetchFn - `(params) => Promise<response>`
 * @param {Array}  [options.defaultSort]  - DataGrid sortModel, e.g. `[{ field: 'name', sort: 'asc' }]`
 * @param {Array}  [options.searchFields] - field names sent as `text_search` prefix
 * @param {Object} [options.extraParams]  - additional query params merged into every request
 * @param {number} [options.defaultPageSize=25]
 * @param {boolean} [options.infiniteScroll=false] - use lazy loading instead of pagination
 * @param {string|null} [options.persistKey=null] - fills `grid:<persistKey>:ct<customerTypeId>:<sort|search|pagination|filters>`,
 *   the sessionStorage keys this hook persists that state under. Scoped to the customer type in scope, since a grid's
 *   columns and filters follow it.
 * @param {Object|null} [options.defaultFilters=null] - initial filter object before any sessionStorage value loads,
 *   for the `filters`/`setFilters` pair this hook owns. Session-scoped like sort/search/pagination: a refresh keeps
 *   it, a new tab or session starts clean. Caller state, not wired into the fetch — fold the current value into
 *   `extraParams` to have it drive requests.
 * @param {string|null} [options.tiebreakerField='id'] - field appended as the final sort key for deterministic
 *   paging. Defaults to `id`. Pass a falsy value (e.g. `null`) to send no tiebreaker — needed for endpoints that
 *   can't order by `id` (e.g. Vantage status history, whose primary key is `history_id`, not `id`).
 * @param {Function|null} [options.paramSerializer=null] - optional serializer `(params) => params` applied to the
 *   raw query-param object before the fetch. When provided, wins over `paramStyle`. When absent, falls back to the
 *   `paramStyle` branch (existing behaviour for all current callers).
 * @param {Object} [options.apiRef=null] - optional external MUI X apiRef to use instead of an internally created
 *   one, so a parent/sibling (e.g. a custom search slot) can share the grid's apiRef.
 */
export default function useServerDataGrid({
  fetchFn,
  defaultSort = [],
  searchFields = [],
  extraParams = EMPTY_OBJECT,
  defaultPageSize = 25,
  infiniteScroll = false,
  persistKey = null,
  defaultFilters = null,
  tiebreakerField = 'id',
  paramStyle = 'drf',
  paramSerializer = null,
  apiRef: providedApiRef = null,
}) {
  // Callers that need the grid's apiRef outside the hook (e.g. a custom search
  // slot wiring useGridQuickFilter, or a component that owns the grid) can pass
  // their own ref; otherwise the hook owns one. The internal ref is always
  // created to keep hook order stable.
  const ownApiRef = useGridApiRef();
  const apiRef = providedApiRef ?? ownApiRef;
  const pageSize = infiniteScroll ? LAZY_PAGE_SIZE : defaultPageSize;

  const [sortModel, setSortModel] = usePersistedState(useGridSessionKey(persistKey, 'sort'), defaultSort);
  const [searchText, setSearchText] = usePersistedState(useGridSessionKey(persistKey, 'search'), '');
  const paginationKey = useGridSessionKey(persistKey, 'pagination');
  const [paginationModel, setPaginationModelRaw] = usePersistedState(infiniteScroll ? null : paginationKey, {
    page: 0,
    pageSize,
  });
  const [loading, setLoading] = useState(false);
  const [totalRowCount, setTotalRowCount] = useState(null);
  const loadingRef = useRef(false);
  const activeRequestsRef = useRef(0);

  // Filters live in sessionStorage under the same scope as sort/search/
  // pagination: cleared when the tab closes, never carried to another
  // session or account.
  const [filters, setFilters] = usePersistedState(useGridSessionKey(persistKey, 'filters'), defaultFilters);

  // Counter to force dataSource recreation (clears grid cache) on manual refresh
  const [refreshKey, setRefreshKey] = useState(0);

  // Tracks whether data has arrived for the current dataSource lifecycle.
  // Used in lazy-loading mode to show the loading overlay only for the
  // initial/reset fetch (filter change, refresh) — not for scroll fetches.
  const hasDataRef = useRef(false);

  // Stable refs to avoid unnecessary dataSource recreation
  const searchFieldsRef = useRef(searchFields);
  searchFieldsRef.current = searchFields;

  // Build the dataSource object.
  // The grid calls getRows() with current sortModel, filterModel, and range (start/end)
  // or paginationModel. We map these to Django REST params.
  // Recreates when extraParams change (clears cache, triggers refetch) or on manual refresh.
  const dataSource = useMemo(() => {
    // Reset lifecycle refs whenever dataSource is recreated (filters/sort/refresh changed)
    hasDataRef.current = false;

    return {
      getRows: async params => {
        if (!fetchFn) return { rows: [], rowCount: 0 };

        // Sort (multi-sort as a comma list) and quick-filter search are mapped by
        // the shared builder, which the CSV export path reads too — the file a
        // user exports is the list they are looking at. The `tiebreakerField`
        // (default `id`) it appends keeps LIMIT/OFFSET paging deterministic when
        // the primary sort column has ties (e.g. NULL name_number for names
        // without digits); without it, pages can return overlapping/missing rows,
        // leaving skeleton rows that never resolve.
        const queryParams = buildGridListParams({
          extraParams,
          sortModel: params.sortModel,
          quickFilterValues: params.filterModel?.quickFilterValues,
          searchFields: searchFieldsRef.current,
          tiebreakerField,
        });

        // Map range to offset/limit
        if (infiniteScroll) {
          queryParams.offset = params.start;
          // Always fetch at least a full LAZY_PAGE_SIZE chunk. The grid asks
          // only for what fits its viewport (e.g. 49 rows) — if the dataset's
          // tail falls inside the requested range, those rows would render as
          // skeletons that never resolve, because lazy loading is scroll-
          // triggered and there's nothing past them to scroll into.
          queryParams.limit = Math.max(params.end - params.start, LAZY_PAGE_SIZE);
        } else {
          queryParams.offset = params.paginationModel.page * params.paginationModel.pageSize;
          queryParams.limit = params.paginationModel.pageSize;
        }

        // Serialize params for the wire format. When a paramSerializer is
        // injected (via DataGridConfigProvider → EntityGrid), it wins.
        // Otherwise fall back to the paramStyle branch so all existing callers
        // (which don't pass paramSerializer) are completely unaffected.
        const requestParams = paramSerializer
          ? paramSerializer(queryParams)
          : paramStyle === 'odl-app'
            ? toJsonApiParams(queryParams)
            : queryParams;

        // In lazy-loading mode the grid renders skeleton rows for unfetched
        // ranges during scroll — we must NOT set `loading` then because the
        // overlay removes placeholders and makes the scrollbar jump.
        // We DO show the overlay for the first fetch after a dataSource reset
        // (filter/sort/refresh change) so users see feedback while stale data
        // is replaced.
        const showOverlay = !infiniteScroll || !hasDataRef.current;
        if (showOverlay) {
          activeRequestsRef.current += 1;
          if (!loadingRef.current) {
            loadingRef.current = true;
            setLoading(true);
          }
        }

        try {
          const res = await fetchFn(requestParams);
          let { data, count } = unwrapGridEnvelope(res);

          // Pull a small remaining tail inline. Lazy loading is scroll-
          // triggered, so a tail that's already visible in the viewport
          // (because nothing's left to scroll into) would otherwise stay
          // as skeleton rows forever. Pulling here means the grid never
          // sees a tail < LAZY_PAGE_SIZE that isn't already filled.
          if (infiniteScroll) {
            let fetched = params.start + data.length;
            while (fetched < count && count - fetched < LAZY_PAGE_SIZE) {
              const tailRes = await fetchFn({
                ...requestParams,
                offset: fetched,
                limit: count - fetched,
              });
              const tail = unwrapGridEnvelope(tailRes);
              if (tail.data.length === 0) break;
              data = data.concat(tail.data);
              count = tail.count ?? count;
              fetched = params.start + data.length;
            }
          }

          setTotalRowCount(count);

          // Position-based IDs prevent duplicate-key corruption from unstable server sort
          // in lazy loading mode where overlapping requests can shift rows across pages.
          const rows = infiniteScroll ? data.map((row, i) => ({ ...row, [GRID_IDX_KEY]: params.start + i })) : data;

          if (infiniteScroll) {
            hasDataRef.current = true;
            // Omit rowCount so the grid runs MUI's infinite-loading mode
            // (fetch the next page when the scroll reaches the bottom).
            // Reporting a known total here selects viewport/skeleton mode,
            // which stops fetching after the first page (mui/mui-x#21620).
            // The real total still feeds the toolbar via setTotalRowCount.
            return { rows };
          }
          return { rows, rowCount: count };
        } finally {
          if (showOverlay) {
            activeRequestsRef.current -= 1;
            if (activeRequestsRef.current === 0 && loadingRef.current) {
              loadingRef.current = false;
              setLoading(false);
            }
          }
        }
      },
    };
  }, [fetchFn, extraParams, infiniteScroll, refreshKey, searchText, tiebreakerField, paramStyle, paramSerializer]);

  // Controlled filterModel — maps DataGrid quick filter to searchText
  const filterModel = useMemo(
    () => ({
      items: [],
      quickFilterValues: searchText ? searchText.split(' ') : [],
    }),
    [searchText],
  );

  const handleFilterModelChange = useCallback(
    model => {
      const quickFilterText = (model.quickFilterValues || []).join(' ');
      setSearchText(quickFilterText);
      if (!infiniteScroll) {
        setPaginationModelRaw(prev => ({ ...prev, page: 0 }));
      }
    },
    [infiniteScroll, setPaginationModelRaw, setSearchText],
  );

  const handleSortModelChange = useCallback(
    model => {
      setSortModel(model);
      if (!infiniteScroll) {
        setPaginationModelRaw(prev => ({ ...prev, page: 0 }));
      }
    },
    [infiniteScroll, setPaginationModelRaw, setSortModel],
  );

  const handlePaginationModelChange = useCallback(
    model => {
      setPaginationModelRaw({
        page: model?.page ?? 0,
        pageSize: model?.pageSize ?? pageSize,
      });
    },
    [pageSize, setPaginationModelRaw],
  );

  useEffect(() => {
    if (!infiniteScroll && paginationModel.pageSize !== pageSize) {
      setPaginationModelRaw(prev => ({ ...prev, pageSize }));
    }
  }, [infiniteScroll, pageSize, paginationModel.pageSize, setPaginationModelRaw]);

  // Manual refresh — recreates dataSource to clear cache and trigger refetch
  const refresh = useCallback(() => {
    setRefreshKey(c => c + 1);
  }, []);

  // Stable getRowId that prefers position-based _gridIdx (infinite scroll)
  const getRowId = useCallback(
    row => (infiniteScroll && row?.[GRID_IDX_KEY] != null ? row[GRID_IDX_KEY] : row?.id),
    [infiniteScroll],
  );

  return {
    // DataGridPro props to spread
    dataSource,
    apiRef,
    sortModel,
    setSortModel: handleSortModelChange,
    filterModel,
    onFilterModelChange: handleFilterModelChange,
    paginationModel,
    onPaginationModelChange: handlePaginationModelChange,
    setPaginationModel: setPaginationModelRaw,
    lazyLoading: infiniteScroll,
    getRowId,
    // Utilities
    loading,
    totalRowCount,
    refresh,
    filters,
    setFilters,
  };
}
